import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { CATEGORY_META, tx } from "@/lib/i18n";
import { ChevronRight, ExternalLink, Download, Globe, AlertTriangle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

function Section({ title, children }) {
  return (
    <section className="border border-slate-200 rounded-md bg-white overflow-hidden">
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
        <h2 className="font-display font-semibold text-slate-900 text-sm tracking-wide uppercase">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function KV({ k, v }) {
  if (!v) return null;
  return (
    <div className="grid grid-cols-3 gap-3 py-2.5 border-b border-slate-100 last:border-0 text-sm">
      <div className="text-slate-500 col-span-1">{k}</div>
      <div className="col-span-2 font-medium text-slate-900 font-mono-tabular">{v}</div>
    </div>
  );
}

export default function PostDetail() {
  const { slug } = useParams();
  const { lang } = useLang();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/posts/${slug}`).then((r) => setPost(r.data)).catch(() => setError("not-found"));
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    const title = (lang === "hi" && post.title_hi) ? post.title_hi : post.title_en;
    const seoTitle = (lang === "hi" && post.title_hi) ? post.title_hi : (post.seo_title || title);
    document.title = `${seoTitle} — Naukri Suchna`;
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
      el.content = content;
    };
    setMeta("description", post.seo_description || `${post.organization || ""} — ${title}`);
    setMeta("keywords", (post.keywords || []).join(", "));
  }, [post, lang]);

  if (error) return <div className="max-w-3xl mx-auto p-10 text-center" data-testid="post-404">Post not found.</div>;
  if (!post) return <div className="max-w-5xl mx-auto p-10">Loading…</div>;

  const meta = CATEGORY_META[post.category];
  const title = (lang === "hi" && post.title_hi) ? post.title_hi : post.title_en;

  return (
    <article className="pb-12" data-testid="post-detail">
      <div className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
          <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-4" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">{tx("nav.home", lang)}</Link>
            <ChevronRight size={12} />
            <Link to={`/${post.category}`} className="hover:text-white">{meta[lang === "hi" ? "hi" : "en"]}</Link>
            <ChevronRight size={12} />
            <span className="text-white line-clamp-1">{title}</span>
          </nav>
          <div className="text-[11px] font-bold tracking-[0.16em] uppercase text-orange-400 mb-3">
            {meta[lang === "hi" ? "hi" : "en"]}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
            {title}
          </h1>
          {post.organization && (
            <p className="mt-3 text-slate-300 text-sm">{post.organization}</p>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {post.is_demo && (
          <div className="mb-6 flex items-start gap-3 p-4 border border-orange-200 bg-orange-50 rounded-md text-sm text-orange-900" data-testid="demo-banner">
            <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
            <p>{tx("ui.demo_disclaimer", lang)}</p>
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Section title={tx("ui.important_dates", lang)}>
              <KV k={lang === "hi" ? "आवेदन प्रारंभ" : "Application Start"} v={post.application_start_date} />
              <KV k={tx("ui.last_date", lang)} v={post.application_last_date} />
              <KV k={lang === "hi" ? "परीक्षा तिथि" : "Exam Date"} v={post.exam_date} />
            </Section>

            <Section title={lang === "hi" ? "मुख्य जानकारी" : "Key Information"}>
              <KV k={tx("ui.advertisement_no", lang)} v={post.advertisement_number} />
              <KV k={tx("ui.total_vacancies", lang)} v={post.total_vacancies} />
              <KV k={tx("ui.application_fee", lang)} v={post.application_fee} />
              <KV k={tx("ui.age_limit", lang)} v={post.age_limit} />
              <KV k={tx("ui.qualification", lang)} v={post.educational_qualification} />
              <KV k={tx("ui.salary", lang)} v={post.salary} />
              <KV k={lang === "hi" ? "राज्य" : "State"} v={post.state} />
            </Section>

            {post.vacancy_details?.length > 0 && (
              <Section title={tx("ui.vacancy_details", lang)}>
                <table className="w-full text-sm">
                  <tbody>
                    {post.vacancy_details.map((v, i) => (
                      <tr key={i} className="border-b border-slate-100 last:border-0">
                        <td className="py-2 text-slate-700">{v.name}</td>
                        <td className="py-2 text-right font-mono-tabular font-semibold text-slate-900">{v.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>
            )}

            {post.category_wise_vacancy?.length > 0 && (
              <Section title={tx("ui.category_wise", lang)}>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  {post.category_wise_vacancy.map((v, i) => (
                    <div key={i} className="border border-slate-200 rounded-sm p-3 text-center">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">{v.name}</div>
                      <div className="mt-0.5 font-mono-tabular font-bold text-lg text-slate-900">{v.count}</div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {post.selection_process?.length > 0 && (
              <Section title={tx("ui.selection", lang)}>
                <ol className="space-y-2 list-decimal list-inside text-sm text-slate-700">
                  {post.selection_process.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              </Section>
            )}

            {post.how_to_apply && (
              <Section title={tx("ui.how_to_apply", lang)}>
                <div className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{post.how_to_apply}</div>
              </Section>
            )}

            {post.faqs?.length > 0 && (
              <Section title={tx("ui.faqs", lang)}>
                <Accordion type="single" collapsible className="w-full">
                  {post.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`}>
                      <AccordionTrigger className="text-left font-medium text-slate-900">{f.question}</AccordionTrigger>
                      <AccordionContent className="text-slate-600">{f.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Section>
            )}
          </div>

          <aside className="space-y-4">
            <div className="border border-slate-200 rounded-md bg-white p-5 sticky top-32">
              <h3 className="font-display font-semibold text-slate-900 text-sm tracking-wide uppercase mb-4 pb-2 border-b border-slate-100">
                {tx("ui.important_links", lang)}
              </h3>
              <div className="space-y-2">
                {post.apply_online_url && (
                  <a href={post.apply_online_url} target="_blank" rel="noopener noreferrer" data-testid="link-apply"
                    className="flex items-center justify-between w-full px-3 py-2.5 bg-slate-900 text-white rounded-sm text-sm font-semibold hover:bg-slate-800 transition-colors">
                    <span>{tx("ui.apply_online", lang)}</span><ExternalLink size={14} />
                  </a>
                )}
                {post.notification_pdf_url && (
                  <a href={post.notification_pdf_url} target="_blank" rel="noopener noreferrer" data-testid="link-pdf"
                    className="flex items-center justify-between w-full px-3 py-2.5 bg-orange-600 text-white rounded-sm text-sm font-semibold hover:bg-orange-700 transition-colors">
                    <span>{tx("ui.download_pdf", lang)}</span><Download size={14} />
                  </a>
                )}
                {post.official_website_url && (
                  <a href={post.official_website_url} target="_blank" rel="noopener noreferrer" data-testid="link-official"
                    className="flex items-center justify-between w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm font-semibold text-slate-900 hover:bg-slate-50 transition-colors">
                    <span>{tx("ui.official_website", lang)}</span><Globe size={14} />
                  </a>
                )}
                {post.important_links?.map((l, i) => (
                  <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`link-extra-${i}`}
                    className="flex items-center justify-between w-full px-3 py-2 border border-slate-200 rounded-sm text-sm text-slate-700 hover:border-slate-400 transition-colors">
                    <span>{l.label}</span><ExternalLink size={12} />
                  </a>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </article>
  );
}
